import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
import CloseIcon from '@mui/icons-material/Close'
import { mainApi } from '../../../store/services/mainApi';
import { digitalAppApi } from './../../../store/services/digitalAppApi';

const OrderPageLogistic = () => {
  const [orderIdParams, setOrderIdParams] = useState(location.pathname.split('/').pop())
  const {
    data: orderData,
    isLoading,
    isError,
  } = mainApi.useGetOrdersByIdQuery(orderIdParams, {
    pollingInterval: 50000,
  })

  useEffect(() => {
    if (orderData) {
      setOrder(orderData)
    }
  }, [orderData])

  const [order, setOrder] = useState(orderData)
  const [deliveryDate, setDeliveryDate] = useState(() => {
    // Предустанавливаем дату из первого заказа, если есть
    if (
      order?.length > 0 &&
      order?.deliveryDate
    ) {
      const orderDate =
        order.deliveryDate.split(',')[0]
      return dayjs(orderDate, 'DD.MM.YYYY')
    }
    return dayjs()
  })
  const [albaranNumber, setAlbaranNumber] = useState('')
  const hasValidOrders =
    order?.items && order.items.length > 0

  useEffect(() => {

    const orderId = order?._id?.slice(-6) || '000000'
    const tradeName = order?.tradeName || 'TPO'
    const dateStr = deliveryDate.format('YYYYMMDD')
    setAlbaranNumber(`${orderId}-${tradeName}-${dateStr}`)
  }, [order, deliveryDate])

  // Обновляем дату доставки при изменении заказа - только когда модалка открыта
  useEffect(() => {
    if (!order?.deliveryDate) return

    const orderDate = order.deliveryDate.split(',')[0]
    const newDate = dayjs(orderDate, 'DD.MM.YYYY')
    if (newDate.isValid()) {
      setDeliveryDate(newDate)
    }
  }, [order])

  const totalItems = order?.items?.length || 0

  const totalValue =
    order?.items?.reduce((total, item) => {
      const quantity = Number(item?.quantity || 0)
      const price = Number(
        item?.ourPrice || item?.marketPriceCP || 0,
      )
      return total + quantity * price
    }, 0) || 0

  const orderId = order?._id
  // console.log(orderId)
  const { 
    data: albaranData, 
    isLoading: isLoadingAlbaran, 
    isError: isErrorAlbaran,
    isSuccess
  } = digitalAppApi.useGetAlbaranByIdQuery({id: orderId}, { 
    skip: !(orderId), // Пропускаем запрос, если модалка закрыта или нет ID
  });

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  
  useEffect(() => {
    let urlToRevoke = null;

    // 💡 ПРОВЕРКА: Проверяем, что запрос успешен (isSuccess) 
    // и что данные являются Blob (albaranData instanceof Blob)
    if (isSuccess && albaranData instanceof Blob) {
      urlToRevoke = URL.createObjectURL(albaranData);
      setPdfBlobUrl(urlToRevoke);
    } else {
      setPdfBlobUrl(null);
    }

    // Очистка
    return () => {
      if (urlToRevoke) {
        URL.revokeObjectURL(urlToRevoke);
      }
    };
  }, [albaranData, isSuccess]);

  // console.log("PDF Data:", albaranData);
  // console.log("Is Success:", isSuccess);
  // console.log("Is Blob:", albaranData instanceof Blob); 
  // console.log("PDF URL:", pdfBlobUrl);

  const handleDownloadServerPdf = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = `albaran_${orderId}_server.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenServerPdf = () => {
    if (pdfBlobUrl) {
      window.open(pdfBlobUrl, '_blank');
    }
  };

  if (!order) return null
  return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          boxSizing: 'border-box',
          p: 3,
        }}
      >
        <Box 
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
        >
          <Typography
            id='albaran-modal-title'
            variant='h6'
            component='h2'
            gutterBottom
          >
            Print Albaran
          </Typography>
          <IconButton
            className='driver-comments-modal__close'
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Order ID: {orderId?.slice(-6) || 'N/A'}
          </Typography>
          
          <Typography 
            variant='body2' 
            color={
              isLoadingAlbaran ? 'warning.main' : 
              isErrorAlbaran ? 'error.main' : 
              pdfBlobUrl ? 'success.main' : 'text.secondary'
            }
          >
            PDF Status: 
            {isLoadingAlbaran ? (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress color='warning' size={14} /> Loading...
              </Box>
            ) : isErrorAlbaran ? 'Error fetching PDF' : pdfBlobUrl ? 'Ready' : 'Waiting for data'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 2 }}>
          
          {pdfBlobUrl && !isLoadingAlbaran && (
            <>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant='outlined'
                  onClick={handleDownloadServerPdf}
                  sx={{ minWidth: 120 }}
                >
                  Download PDF
                </Button>
                <Button
                  variant='outlined'
                  onClick={handleOpenServerPdf}
                  sx={{ minWidth: 120 }}
                >
                  Open in New Tab
                </Button>
              </Box>
            </>
          )}

          {isErrorAlbaran && (
            <Typography color='error'>
              Error: Failed to fetch document. 
              {albaranData?.error?.message}
            </Typography>
          )}
        </Box>
        {pdfBlobUrl && !isLoadingAlbaran && (
          <Box 
            sx={{ 
              height: { xs: '60dvh', sm: '70dvh' }, 
              width: '100%', 
              mt: 2, 
              border: '1px solid #ccc' 
            }}
          >
            <iframe
              src={pdfBlobUrl} // Используем URL, полученный из серверного Blob
              title="Server Albaran PDF Viewer"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          </Box>
        )}

        {/* Скрытые поля - данные заполняются автоматически */}
        <Box sx={{ display: 'none' }}>
          
        </Box>
      </Box>
  )
}

export default OrderPageLogistic
